import {getCookie, setCookie} from 'methods/utils';

const STORAGE_KEY = 'pt-theme';
const COOKIE_PREF_KEY = 'pt-theme-pref';
const THEMES = ['light', 'dark'];
const THEME_PREFS = ['light', 'dark', 'system'];
const COOKIE_DAYS = 365;
const THEME_COLOR_LIGHT = '#FFFFFF';
const THEME_COLOR_DARK = '#1A1625';

function isMobileSite() {
    if (document.body && document.body.dataset.is_mobile === '1') {
        return true;
    }

    return document.documentElement.getAttribute('data-is-mobile') === '1';
}

function getSystemTheme() {
    if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
    }

    return 'light';
}

function resolveThemePreference(pref) {
    if (pref === 'dark' || pref === 'light') {
        return pref;
    }

    return getSystemTheme();
}

function getStoredThemePreference() {
    try {
        const fromStorage = localStorage.getItem(COOKIE_PREF_KEY);
        if (THEME_PREFS.includes(fromStorage)) {
            return fromStorage;
        }
    } catch (e) {}

    const value = getCookie(COOKIE_PREF_KEY);
    if (THEME_PREFS.includes(value)) {
        return value;
    }

    if (isMobileSite()) {
        return 'system';
    }

    return null;
}

function getStoredTheme() {
    try {
        const value = localStorage.getItem(STORAGE_KEY);
        if (THEMES.includes(value)) {
            return value;
        }
    } catch (e) {}

    const pref = getStoredThemePreference();
    if (pref === 'dark' || pref === 'light') {
        return pref;
    }

    return 'light';
}

function syncThemeColorMeta(theme) {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
        return;
    }

    meta.content = theme === 'dark' ? THEME_COLOR_DARK : THEME_COLOR_LIGHT;
}

function syncThemeToggleButtons(theme) {
    const isDark = theme === 'dark';

    document.querySelectorAll('.js-theme-toggle').forEach(($button) => {
        $button.setAttribute('aria-pressed', isDark ? 'true' : 'false');
        $button.setAttribute(
            'aria-label',
            isDark ? 'Включить светлую тему' : 'Включить тёмную тему'
        );
        $button.title = isDark ? 'Светлая тема' : 'Тёмная тема';
        $button.classList.toggle('is-dark', isDark);
    });

    document.querySelectorAll('.js-theme-menu-open').forEach(($button) => {
        $button.classList.toggle('is-dark', isDark);
    });
}

function syncThemeSheetPreference(pref) {
    document.querySelectorAll('.js-theme-pref').forEach(($input) => {
        $input.checked = $input.value === pref;
    });
}

function applyTheme(theme, options = {}) {
    const previousTheme = document.documentElement.getAttribute('data-theme');
    const next = THEMES.includes(theme) ? theme : 'light';
    document.documentElement.setAttribute('data-theme', next);
    syncThemeColorMeta(next);

    document.dispatchEvent(new CustomEvent('pt-theme-change', {
        detail: {
            theme: next,
            previousTheme,
            userAction: Boolean(options.userAction),
        },
    }));

    syncThemeToggleButtons(next);

    return next;
}

function persistThemePreference(pref) {
    try {
        localStorage.setItem(COOKIE_PREF_KEY, pref);
    } catch (e) {}

    setCookie(COOKIE_PREF_KEY, pref, COOKIE_DAYS);
}

function persistDesktopTheme(theme) {
    try {
        localStorage.setItem(STORAGE_KEY, theme);
        localStorage.setItem(COOKIE_PREF_KEY, theme);
    } catch (e) {}

    setCookie(COOKIE_PREF_KEY, theme, COOKIE_DAYS);
}

function persistMobileThemePreference(pref) {
    persistThemePreference(pref);

    try {
        localStorage.setItem(STORAGE_KEY, resolveThemePreference(pref));
    } catch (e) {}
}

function setMobileThemePreference(pref, options = {}) {
    const nextPref = THEME_PREFS.includes(pref) ? pref : 'system';
    applyTheme(resolveThemePreference(nextPref), {userAction: true});
    persistMobileThemePreference(nextPref);

    if (options.syncSheet !== false) {
        syncThemeSheetPreference(nextPref);
    }

    return nextPref;
}

function toggleTheme() {
    const current = document.documentElement.getAttribute('data-theme') || getStoredTheme();
    const next = current === 'dark' ? 'light' : 'dark';
    applyTheme(next, {userAction: true});
    persistDesktopTheme(next);
    return next;
}

let systemThemeListenerBound = false;

function bindSystemThemeListener() {
    if (systemThemeListenerBound || typeof window.matchMedia !== 'function') {
        return;
    }

    systemThemeListenerBound = true;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
        if (!isMobileSite() || getStoredThemePreference() !== 'system') {
            return;
        }

        applyTheme(getSystemTheme());
    };

    if (typeof media.addEventListener === 'function') {
        media.addEventListener('change', onChange);
        return;
    }

    if (typeof media.addListener === 'function') {
        media.addListener(onChange);
    }
}

function getThemeSheetElements() {
    return {
        $sheet: document.querySelector('.js-theme-sheet'),
        $overlay: document.querySelector('.js-theme-sheet-overlay'),
    };
}

let themeSheetTrigger = null;

function openThemeSheet() {
    const {$sheet, $overlay} = getThemeSheetElements();
    if (!$sheet) {
        return;
    }

    syncThemeSheetPreference(getStoredThemePreference());
    $sheet.classList.add('is-open');
    $sheet.setAttribute('aria-hidden', 'false');

    if ($overlay) {
        $overlay.classList.add('is-open');
        $overlay.setAttribute('aria-hidden', 'false');
    }

    themeSheetTrigger = document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const $close = $sheet.querySelector('.js-theme-sheet-close');
    if ($close instanceof HTMLElement) {
        $close.focus();
    }
}

function closeThemeSheet() {
    const {$sheet, $overlay} = getThemeSheetElements();
    if (!$sheet) {
        return;
    }

    $sheet.classList.remove('is-open');
    $sheet.setAttribute('aria-hidden', 'true');

    if ($overlay) {
        $overlay.classList.remove('is-open');
        $overlay.setAttribute('aria-hidden', 'true');
    }

    if (themeSheetTrigger instanceof HTMLElement) {
        themeSheetTrigger.focus();
    }

    themeSheetTrigger = null;
}

let mobileThemeMenuBound = false;

function initMobileThemeMenu($root = document) {
    bindSystemThemeListener();

    if (mobileThemeMenuBound) {
        syncThemeSheetPreference(getStoredThemePreference());
        return;
    }

    mobileThemeMenuBound = true;

    $root.addEventListener('click', (event) => {
        if (event.target.closest('.js-theme-menu-open')) {
            event.preventDefault();
            openThemeSheet();
            return;
        }

        if (event.target.closest('.js-theme-sheet-close')) {
            event.preventDefault();
            closeThemeSheet();
            return;
        }

        if (event.target.closest('.js-theme-sheet-overlay')) {
            event.preventDefault();
            closeThemeSheet();
        }
    });

    $root.addEventListener('change', (event) => {
        const $input = event.target.closest('.js-theme-pref');
        if (!$input) {
            return;
        }

        setMobileThemePreference($input.value);
    });

    document.addEventListener('keydown', (event) => {
        const {$sheet} = getThemeSheetElements();
        if (event.key !== 'Escape' || !$sheet || !$sheet.classList.contains('is-open')) {
            return;
        }

        event.preventDefault();
        closeThemeSheet();
    });
}

function initMobileTheme() {
    const pref = getStoredThemePreference();
    applyTheme(resolveThemePreference(pref));
    syncThemeSheetPreference(pref);
    bindSystemThemeListener();
    initMobileThemeMenu(document);
}

function initDesktopThemeToggle() {
    applyTheme(getStoredTheme());

    document.querySelectorAll('.js-theme-toggle').forEach(($button) => {
        if ($button.dataset.themeToggleInit === '1') {
            return;
        }

        $button.dataset.themeToggleInit = '1';
        $button.addEventListener('click', (event) => {
            event.preventDefault();
            toggleTheme();
        });
    });
}

function initThemeToggle() {
    if (isMobileSite()) {
        initMobileTheme();
        return;
    }

    initDesktopThemeToggle();
}

export {
    applyTheme,
    getStoredTheme,
    getStoredThemePreference,
    initMobileThemeMenu,
    initThemeToggle,
    resolveThemePreference,
    setMobileThemePreference,
    toggleTheme,
};

export default initThemeToggle;
